import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

// Import the Image EXIF Orientation and Image Preview plugins
// Note: These need to be installed separately
// `npm i filepond-plugin-image-preview filepond-plugin-image-exif-orientation --save`
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReduxState } from 'types';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { MAX_UPLOAD_SIZE_MB } from '../../../consts/consts';
import { AUTH0_PARAMS } from '../../../utils/api/AuthProvider';
import { redirectLoginOptionsGenerator } from '../../../utils/util/utiltsx';
import TextArea from './TextArea';

// Register the plugins
registerPlugin(
  FilePondPluginFileValidateSize,
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview
);

interface Props {
  onUpload?: (data: any) => void;
  onUploadedChange?: (files: any[]) => void;
  callToAction?: string;
  uploadUrl?: string;
  enableDescriptionField?: boolean;
  descriptionFieldPlaceholder?: string;
  currentOrganizationId?: number;
  maxFiles?: number;
  maxFileSizeMb?: number;
}

const MediaUploader: FC<Props> = ({
  callToAction = 'Add screenshot or video',
  descriptionFieldPlaceholder = 'Add a description of the file',
  uploadUrl = process.env.REACT_APP_CONFIRM_API_URL + '/files/activity-image',
  enableDescriptionField = false,
  onUpload,
  onUploadedChange,
  maxFiles = 9,
  maxFileSizeMb = MAX_UPLOAD_SIZE_MB,
  ...props
}) => {
  const effectiveMaxFileSizeMb = Math.min(maxFileSizeMb, MAX_UPLOAD_SIZE_MB);
  const { getAccessTokenSilently, loginWithRedirect } = useAuthProvider();
  const [accessToken, setAccessToken] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const location = useLocation();
  const { formatMessage } = useIntl();

  const currentOrgId = props.currentOrganizationId;

  const maxFileSizeMessage = formatMessage(
    {
      id: 'app.views.widgets.inputs.media_uploader.max_file_size',
      defaultMessage: 'Maximum file size is {maxSize}MB',
    },
    { maxSize: effectiveMaxFileSizeMb }
  );

  const isSignup = location.pathname === '/signup';

  // update access token for file/image uploading as close to actually sending as possible
  // to ensure that if someone leaves and comes back after the token expires,
  // the save will work (NOTE: we update this whenever the model changes to ensure
  // this is ALWAYS up to date)
  useEffect(() => {
    (async () => {
      let token = null;
      try {
        console.debug('[mediauploader][call] getAccessTokenSilently');
        // @ts-expect-error
        token = await getAccessTokenSilently(AUTH0_PARAMS);
        console.debug('[mediauploader][response] getAccessTokenSilently');
      } catch (e) {
        console.debug(e);
        // @ts-expect-error
        if (e.error === 'login_required' || e.error === 'consent_required') {
          console.debug('[mediauploader][call] loginWithRedirect');
          // @ts-expect-error
          loginWithRedirect(redirectLoginOptionsGenerator(location, isSignup));
          console.debug('[mediauploader][response] loginWithRedirect');
        }
        throw e;
      }

      setAccessToken(token);
    })();
  }, [getAccessTokenSilently, location, loginWithRedirect, isSignup]);

  const syncUploadedFiles = useCallback(
    (newUploadedFiles) => {
      setUploadedFiles(newUploadedFiles);
      onUploadedChange?.(newUploadedFiles);
    },
    [onUploadedChange]
  );

  const handleOnUpload = useCallback(
    (response) => {
      onUpload?.(response);
      syncUploadedFiles([...uploadedFiles, response]);
    },
    [onUpload, syncUploadedFiles, uploadedFiles]
  );

  const handleRemoveFile = useCallback(
    (removedFile) => {
      syncUploadedFiles(
        uploadedFiles.filter((it) => it?.media?.id !== removedFile.serverId)
      );
    },
    [syncUploadedFiles, uploadedFiles]
  );

  const handleOnDescriptionChange = useCallback(
    (mediaId) => (e) => {
      syncUploadedFiles(
        uploadedFiles.map((it) =>
          it?.media?.id === mediaId
            ? { ...it, description: e.target.value }
            : it
        )
      );
    },
    [syncUploadedFiles, uploadedFiles]
  );

  return (
    <div className="pb-4">
      <FilePond
        files={files as any}
        onupdatefiles={setFiles as any}
        allowMultiple={true}
        allowReorder={true}
        maxFiles={maxFiles}
        maxFileSize={`${effectiveMaxFileSizeMb}MB`}
        labelMaxFileSize={maxFileSizeMessage}
        labelMaxFileSizeExceeded={formatMessage({
          id: 'app.views.widgets.inputs.media_uploader.file_too_large',
          defaultMessage: 'File is too large',
        })}
        beforeRemoveFile={handleRemoveFile as any}
        server={{
          url: uploadUrl,
          process: {
            method: 'PUT',
            headers: {
              Authorization: 'Bearer ' + accessToken,
            },
            onerror: (response: any) => {
              const errorData = JSON.parse(response);
              toast.error(errorData.message);
            },
            ondata: (formData: any) => {
              formData.append('organization', currentOrgId);
              return formData;
            },
            onload: (response: any) => {
              const parsedFile = JSON.parse(response);
              handleOnUpload(parsedFile);
              return parsedFile?.media?.id;
            },
          } as any,
        }}
        name="file"
        labelIdle={
          '<div class="fe fe-image fs-lg mb-2"></div><div class="row"><div class="col"><span class="filepond--label-action">' +
          callToAction +
          '</span></div></div>' +
          '<div class="text-muted small mt-1">' +
          maxFileSizeMessage +
          '</div>'
        }
      />
      {enableDescriptionField && (
        <>
          {!!uploadedFiles.length && (
            <label className="mb-1 pt-4 form-label">
              <FormattedMessage
                id="app.views.widgets.inputs.media_uploader.files_description"
                defaultMessage="Files Description"
              />
            </label>
          )}
          {uploadedFiles.map((it) => (
            <div key={it.media.id} className="pb-4">
              <small className="form-text text-muted pt-3">
                {formatMessage(
                  {
                    id: 'app.views.widgets.inputs.media_uploader.add_a_description_for_the_file',
                    defaultMessage:
                      'Description for the file <b>{filename}</b>',
                  },
                  {
                    b: (chunks: ReactNode) => (
                      <span className="badge bg-light rounded-pill">
                        {chunks}
                      </span>
                    ),
                    filename: it.link.split('/').pop(),
                  }
                )}
              </small>
              {/* @ts-expect-error */}
              <TextArea
                name={`file_description_${it.media.id}`}
                maxLength={1000}
                minRows={3}
                maxRows={15}
                required={true}
                role=""
                placeholder={descriptionFieldPlaceholder}
                className="form-control"
                onChange={handleOnDescriptionChange(it.media.id)}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};

// These props come from the application's
// state when it is started
const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization } = state;

  return {
    currentOrganizationId: currentOrganization?.id,
  };
};

export default connect(mapStateToProps)(React.memo(MediaUploader));
